import { keys } from "@prismicio/editor-support/Object"
import { isDefined } from "@prismicio/editor-support/Type"
import { Box, Skeleton, Text } from "@prismicio/editor-ui"
import { Fragment, type ReactNode } from "react"

import type { QuotaOrLimit } from "../../app/services/billing"
import { useSubscriptionQuery } from "../billing/useSubscription"
import {
  CurrentPlanCardSubItem,
  type CurrentPlanCardSubItemProps,
  CurrentPlanCardSubItemSkeleton,
} from "./CurrentPlanCardSubItem"
import {
  usageSummaryItemTypes,
  usageSummarySubItemTypes,
} from "./usageSummaryItemTypes"
import { formatValue, type Unit } from "./usageUtils"

export interface CurrentPlanCardItemProps {
  itemTypeKey: keyof typeof usageSummaryItemTypes
}

export function CurrentPlanCardItem(props: CurrentPlanCardItemProps) {
  const { itemTypeKey } = props

  const { data: subscription } = useSubscriptionQuery()

  const itemType = usageSummaryItemTypes[itemTypeKey]
  const { name, unit, unitLabel, formatQuotaDescription } = itemType
  const quota = itemType.getQuota(subscription)
  const limit = itemType.getLimit(subscription)

  const title = formatTitle({ quota, unit, name })
  const description = formatDescription({
    quota,
    unit,
    formatQuotaDescription,
    unitLabel,
    limit,
  })

  return (
    <CurrentPlanCardItemTemplate
      title={
        <Text component="h5" variant="bold">
          {title}
        </Text>
      }
      description={
        <Text color="grey11" variant="small">
          {description}
        </Text>
      }
      link={
        <Text
          href="https://community.prismic.io/t/prismic-api-limits-and-cdn-bandwidth-explained-faq-guide/16707"
          variant="small"
        >
          Learn more (FAQ)
        </Text>
      }
      renderCurrentPlanCardSubItem={(props) => (
        <CurrentPlanCardSubItem {...props} itemTypeKey={itemTypeKey} />
      )}
    />
  )
}

interface CurrentPlanCardItemTemplateProps {
  title: ReactNode
  description: ReactNode
  link: ReactNode
  renderCurrentPlanCardSubItem: (
    props: Pick<CurrentPlanCardSubItemProps, "subItemTypeKey">,
  ) => ReactNode
}

function CurrentPlanCardItemTemplate(props: CurrentPlanCardItemTemplateProps) {
  const { title, description, link, renderCurrentPlanCardSubItem } = props
  return (
    <Box flexDirection="column" gap={16}>
      <Box flexDirection="column" gap={4}>
        {title}
        {description}
        <Box margin={{ top: 12 }}>{link}</Box>
      </Box>
      {keys(usageSummarySubItemTypes).map((subItemTypeKey) => (
        <Fragment key={subItemTypeKey}>
          {renderCurrentPlanCardSubItem({ subItemTypeKey })}
        </Fragment>
      ))}
    </Box>
  )
}

interface FormatTitleOptions {
  quota: QuotaOrLimit
  unit: Unit
  name: string
}

function formatTitle(options: FormatTitleOptions) {
  const { quota, unit, name } = options

  if (quota !== "unlimited") {
    const formattedQuota = formatValue(quota, { unit })
    return [formattedQuota, name].join(" ")
  }

  return name
}

interface FormatDescriptionOptions {
  quota: QuotaOrLimit
  unit: Unit
  formatQuotaDescription: (formattedQuota: string) => string
  unitLabel: string
  limit: QuotaOrLimit
}

function formatDescription(options: FormatDescriptionOptions) {
  const { quota, unit, formatQuotaDescription, unitLabel, limit } = options

  let quotaSentence
  if (quota !== "unlimited") {
    const formattedQuota = formatValue(quota, { unit, compactDisplay: "long" })
    quotaSentence = `Your plan includes ${formatQuotaDescription(formattedQuota)}.`
  }

  if (quota === limit) return quotaSentence

  const overageSentence = `If you reach the consumption limit of your plan, you'll be billed for each ${unitLabel} over-consumed.`

  let limitSentence
  if (limit !== "unlimited") {
    const formattedLimit = formatValue(limit, { unit, compactDisplay: "long" })
    limitSentence = `Paid overages allowed up to ${formattedLimit}.`
  }

  const quotaAndOverageParagraph = [quotaSentence, overageSentence]
    .filter(isDefined)
    .join(" ")

  return [quotaAndOverageParagraph, limitSentence].filter(isDefined).join("\n")
}

export function CurrentPlanCardItemSkeleton() {
  return (
    <CurrentPlanCardItemTemplate
      title={<Skeleton width={138} height={24} />}
      description={<Skeleton height={16} />}
      link={<Skeleton width={100} height={16} />}
      renderCurrentPlanCardSubItem={CurrentPlanCardSubItemSkeleton}
    />
  )
}
